<template>
  <div class="c2c-desc">
    <small class="c2c-desc-hint">{{ props.hint }}</small>

    <div
      v-for="(row, i) in modelValue"
      :key="row.lang"
      class="c2c-desc-row"
      :ref="el => setRowEl(row.lang, el as HTMLElement | null)"
    >
      <div class="c2c-desc-row-head">
        <label :for="textareaId(i)" class="c2c-desc-lang">
          {{ nativeName(row.lang) }}
          <span class="c2c-desc-code">{{ row.lang }}</span>
        </label>
        <CdxButton
          type="button"
          weight="quiet"
          size="small"
          :aria-label="t('form.description_remove_aria', { language: nativeName(row.lang) })"
          @click="removeRow(i)"
        >
          <Trash2 :size="16" />
        </CdxButton>
      </div>

      <textarea
        :id="textareaId(i)"
        :ref="el => setTextareaEl(row.lang, el as HTMLTextAreaElement | null)"
        :value="row.text"
        :rows="props.rows"
        :placeholder="props.placeholder"
        :maxlength="props.maxLength"
        @input="onTextInput(i, row.lang, ($event.target as HTMLTextAreaElement).value)"
      />

      <div class="c2c-desc-row-foot">
        <small>
          {{ row.text.length > props.softLimit
            ? t('form.description_count_long', { n: row.text.length })
            : t('form.description_count', { n: row.text.length }) }}
        </small>

        <!-- Framed, not quiet: as a quiet button this read as a line of
             text and people did not realise translation was on offer. -->
        <CdxButton
          v-if="canSuggestFor(row.lang)"
          type="button"
          action="progressive"
          weight="normal"
          size="small"
          class="c2c-desc-suggest-btn"
          :disabled="busyLangs.has(row.lang)"
          @click="suggest(row.lang)"
        >
          <Languages :size="16" />
          {{ busyLangs.has(row.lang)
            ? t('form.description_translating')
            : (row.text.trim()
                ? t('form.description_suggest_replace')
                : t('form.description_suggest')) }}
        </CdxButton>
      </div>

      <small v-if="errors.get(row.lang)" class="c2c-desc-error" role="alert">
        {{ errors.get(row.lang) }}
      </small>
      <small v-else-if="machineTranslated.has(row.lang)" class="c2c-desc-mtnote">
        {{ t('form.description_mt_note', { language: nativeName(sourceRow?.lang || '') }) }}
      </small>
    </div>

    <div class="c2c-desc-add">
      <label :for="pickerId" class="c2c-desc-add-label">
        {{ props.addLabel }}
      </label>

      <div class="c2c-desc-add-row">
        <div class="c2c-desc-picker">
          <input
            :id="pickerId"
            v-model="query"
            type="text"
            role="combobox"
            autocomplete="off"
            spellcheck="false"
            :placeholder="t('form.description_add_placeholder')"
            :aria-expanded="isOpen && matches.length > 0"
            aria-autocomplete="list"
            :aria-controls="listId"
            :aria-activedescendant="activeIndex >= 0 ? optionId(activeIndex) : undefined"
            @input="onQueryInput"
            @focus="isOpen = true"
            @blur="onBlur"
            @keydown.down.prevent="moveDown"
            @keydown.up.prevent="moveUp"
            @keydown.enter.prevent="commitFromKeyboard"
            @keydown.escape="isOpen = false"
          />

          <ul
            v-if="isOpen && matches.length > 0"
            :id="listId"
            class="c2c-desc-suggestions"
            role="listbox"
          >
            <li
              v-for="(entry, i) in matches"
              :key="entry.code"
              :id="optionId(i)"
              role="option"
              :aria-selected="i === activeIndex"
              :class="{
                'c2c-desc-suggestion--active': i === activeIndex,
                'c2c-desc-suggestion--present': isPresent(entry.code)
              }"
              @mousedown.prevent="chooseSuggestion(i)"
            >
              <span class="c2c-desc-suggestion-native">{{ entry.native }}</span>
              <span class="c2c-desc-code">{{ entry.code }}</span>
              <span v-if="isPresent(entry.code)" class="c2c-desc-suggestion-added">
                {{ t('form.description_already_added') }}
              </span>
            </li>
          </ul>

          <small v-if="isOpen && query.trim() && matches.length === 0" class="c2c-desc-nomatch">
            {{ t('form.description_no_matches', { query: query.trim() }) }}
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, useId } from "vue"
import { Languages, Trash2 } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { CdxButton } from "@wikimedia/codex"
import { searchLanguages, nativeName, LANGUAGES, CAPTION_LANGUAGES } from "@/lib/languages"
import {
  translateText, canRequestTranslation, queueTranslation, TranslationError
} from "@/lib/translate"
import type { LocalizedDescription } from "@/lib/types"

const { t } = useI18n()

const props = defineProps<{
  modelValue: LocalizedDescription[]
  /** Explanatory line above the rows. */
  hint: string
  /** Placeholder for each language's textarea. */
  placeholder: string
  /** Label above the add-a-language box. */
  addLabel: string
  /** Hard character cap per row, enforced by the textarea. */
  maxLength: number
  /** Length past which the counter switches to its "getting long" wording. */
  softLimit: number
  rows: number
  /**
   * Captions are Wikibase MediaInfo labels, and Wikibase accepts a
   * different set of language codes than Commons has {{lang}} templates
   * for. When true, the picker offers only caption-capable languages.
   */
  captionsOnly?: boolean
}>()
const emit = defineEmits<{ "update:modelValue": [LocalizedDescription[]] }>()

// useId() is unique per component instance and stable across renders, so
// the caption editor and the description editor cannot collide on DOM ids.
const uid = useId()
const pickerId = "desc-add-" + uid
const listId = "desc-list-" + uid
const textareaId = (i: number) => "desc-text-" + uid + "-" + i
const optionId = (i: number) => "desc-opt-" + uid + "-" + i

/* ---- element refs, so adding a language can focus its row ---- */

const rowEls = new Map<string, HTMLElement>()
const textareaEls = new Map<string, HTMLTextAreaElement>()

function setRowEl(lang: string, el: HTMLElement | null) {
  if (el) rowEls.set(lang, el); else rowEls.delete(lang)
}
function setTextareaEl(lang: string, el: HTMLTextAreaElement | null) {
  if (el) textareaEls.set(lang, el); else textareaEls.delete(lang)
}

/* ---- picker state ---- */

const query = ref("")
const isOpen = ref(false)
const activeIndex = ref(-1)
/** Set when the user explicitly picks a suggestion; cleared on typing. */
const chosenCode = ref("")

const matches = computed(() =>
  searchLanguages(query.value, 60, { captionsOnly: props.captionsOnly }))

/**
 * The language Enter will commit: the highlighted row if the user arrowed
 * to one, else an exact code typed into the box, else the top match. So
 * typing "ta" and pressing Enter adds Tamil without touching the list.
 */
function keyboardTarget(): string {
  if (chosenCode.value) return chosenCode.value
  const q = query.value.trim().toLowerCase()
  if (!q) return ""
  const pool = props.captionsOnly ? CAPTION_LANGUAGES : LANGUAGES
  const exact = pool.find(l => l.code === q)
  if (exact) return exact.code
  return matches.value[0]?.code ?? ""
}

function isPresent(code: string): boolean {
  return props.modelValue.some(d => d.lang === code)
}

function onQueryInput() {
  chosenCode.value = ""
  activeIndex.value = -1
  isOpen.value = true
}

function onBlur() {
  // Delay so a mousedown on a suggestion still registers.
  setTimeout(() => { isOpen.value = false }, 150)
}

function moveDown() {
  if (matches.value.length === 0) return
  isOpen.value = true
  activeIndex.value = Math.min(activeIndex.value + 1, matches.value.length - 1)
  chosenCode.value = matches.value[activeIndex.value].code
}
function moveUp() {
  if (matches.value.length === 0) return
  activeIndex.value = Math.max(activeIndex.value - 1, -1)
  chosenCode.value = activeIndex.value >= 0 ? matches.value[activeIndex.value].code : ""
}

/** Clicking a language in the list is the commit - there is no second step. */
function chooseSuggestion(i: number) {
  addLanguage(matches.value[i].code)
}

/** Enter commits whatever the keyboard is currently pointing at. */
function commitFromKeyboard() {
  addLanguage(keyboardTarget())
}

function resetPicker() {
  query.value = ""
  chosenCode.value = ""
  activeIndex.value = -1
  isOpen.value = false
}

/* ---- machine-translation suggestions ---- */

/** Languages with a request in flight, and the last error per language. */
const busyLangs = ref(new Set<string>())
const errors = ref(new Map<string, string>())
/** Rows whose current text came from MinT rather than from the contributor. */
const machineTranslated = ref(new Set<string>())

/**
 * The row a suggestion is translated FROM: the first one the contributor
 * actually wrote something in. Everything else can be derived from it.
 */
const sourceRow = computed(() =>
  props.modelValue.find(d => typeof d?.text === "string" && d.text.trim()))

function canSuggestFor(lang: string): boolean {
  const src = sourceRow.value
  return !!src && canRequestTranslation(src.lang, lang)
}

function messageForError(err: unknown, from: string, to: string): string {
  if (err instanceof TranslationError && err.code === "unsupported-pair") {
    return t("form.description_translate_unsupported", {
      from: nativeName(from), to: nativeName(to)
    })
  }
  if (err instanceof TranslationError && err.code === "rate-limited") {
    return t("form.description_translate_busy")
  }
  const reason = err instanceof Error ? err.message : String(err)
  return t("form.description_translate_failed", { reason })
}

/**
 * Translations run strictly one at a time, for two independent reasons:
 *
 *  - Correctness. updateText() rebuilds from props.modelValue, but Vue
 *    patches child props on the parent's *scheduled* re-render. Two
 *    requests resolving in the same tick would both read the pre-update
 *    array, and the second emit would silently discard the first
 *    translation. Awaiting nextTick() between writes closes that window.
 *  - The service. cxserver answered 429 while translating into several
 *    languages back to back, which is this feature's normal use.
 *
 * The queue lives in @/lib/translate at module scope, not here, because the
 * caption editor and the description editor are separate instances of this
 * component - a per-instance queue would still let the two of them hit MinT
 * at the same time.
 *
 * The cost is wall-clock time when several languages are queued; each is
 * ~3 s warm, so the queue stays short and every row shows its own pending
 * state meanwhile.
 */
function suggest(lang: string) {
  if (busyLangs.value.has(lang)) return
  // Marked busy immediately, so a queued row reads as pending rather than
  // looking like the click did nothing.
  busyLangs.value.add(lang)
  errors.value.delete(lang)
  void queueTranslation(() => runSuggest(lang))
}

async function runSuggest(lang: string) {
  try {
    // Re-read the source at run time: the contributor may have edited or
    // removed it while this request sat in the queue.
    const src = sourceRow.value
    if (!src || !canRequestTranslation(src.lang, lang)) return

    let text: string
    try {
      text = await translateText({ text: src.text, from: src.lang, to: lang })
    } catch (err) {
      errors.value.set(lang, messageForError(err, src.lang, lang))
      return
    }

    // The row may have been removed while the request was in flight.
    const i = props.modelValue.findIndex(d => d.lang === lang)
    if (i < 0) return

    machineTranslated.value.add(lang)
    updateText(i, text)
    // Let the emit land in props before the next queued translation reads them.
    await nextTick()
  } finally {
    busyLangs.value.delete(lang)
  }
}

/** Drop any per-language state left over from a row that no longer exists. */
function forgetLang(lang: string) {
  errors.value.delete(lang)
  machineTranslated.value.delete(lang)
}

/* ---- row mutations ---- */

/** Always emit fresh objects so callers never share references with us. */
function clone(rows: LocalizedDescription[]): LocalizedDescription[] {
  return rows.map(d => ({ ...d }))
}

function updateText(i: number, text: string) {
  const next = clone(props.modelValue)
  if (!next[i]) return
  next[i].text = text
  emit("update:modelValue", next)
}

/** Typing over a suggestion makes it the contributor's own text. */
function onTextInput(i: number, lang: string, text: string) {
  machineTranslated.value.delete(lang)
  errors.value.delete(lang)
  updateText(i, text)
}

/**
 * Adding a language already in the list is not an error and does not
 * duplicate it - we scroll to and focus the row that already exists, which
 * is what the contributor was looking for anyway.
 */
async function addLanguage(code: string) {
  if (!code) return

  if (props.modelValue.some(d => d.lang === code)) {
    resetPicker()
    await focusRow(code)
    return
  }

  forgetLang(code)
  emit("update:modelValue", [...clone(props.modelValue), { lang: code, text: "" }])
  resetPicker()
  await focusRow(code)
}

/**
 * Removing the last remaining row would leave the contributor with nowhere
 * to type, so it clears that row's text instead and keeps its language.
 */
function removeRow(i: number) {
  const lang = props.modelValue[i]?.lang
  if (lang) forgetLang(lang)

  if (props.modelValue.length <= 1) {
    emit("update:modelValue", [{ lang: lang || "en", text: "" }])
    return
  }
  emit("update:modelValue", clone(props.modelValue).filter((_, idx) => idx !== i))
}

async function focusRow(lang: string) {
  await nextTick()
  rowEls.get(lang)?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  textareaEls.get(lang)?.focus()
}
</script>

<style scoped>
.c2c-desc { display: flex; flex-direction: column; gap: 12px; }
.c2c-desc-hint { color: #54595d; font-size: 12px; line-height: 1.4; }

.c2c-desc-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  background: #f8f9fa;
  border: 1px solid #eaecf0;
  border-radius: 4px;
}
.c2c-desc-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.c2c-desc-lang {
  font-weight: 600;
  font-size: 13px;
  color: #202122;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}
.c2c-desc-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: #54595d;
  background: white;
  border: 1px solid #c8ccd1;
  border-radius: 2px;
  padding: 0 4px;
}

.c2c-desc-row textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #a2a9b1;
  border-radius: 2px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  background: white;
  color: #202122;
}
.c2c-desc-row textarea:focus {
  outline: none;
  border-color: #3366cc;
  box-shadow: inset 0 0 0 1px #3366cc;
}
.c2c-desc-row small { color: #54595d; font-size: 12px; }
.c2c-desc-row-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.c2c-desc-error { color: #b32424 !important; }
.c2c-desc-mtnote { color: #886600 !important; }

.c2c-desc-add { display: flex; flex-direction: column; gap: 4px; }
.c2c-desc-add-label { font-weight: 600; font-size: 13px; color: #202122; }
.c2c-desc-add-row { display: flex; align-items: flex-start; gap: 8px; }
.c2c-desc-picker { position: relative; flex: 1; min-width: 0; }
.c2c-desc-picker input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #a2a9b1;
  border-radius: 2px;
  font-family: inherit;
  font-size: 14px;
  background: white;
  color: #202122;
}
.c2c-desc-picker input:focus {
  outline: none;
  border-color: #3366cc;
  box-shadow: inset 0 0 0 1px #3366cc;
}

.c2c-desc-suggestions {
  position: absolute;
  z-index: 30;
  top: 100%;
  left: 0;
  right: 0;
  margin: 2px 0 0 0;
  padding: 0;
  list-style: none;
  background: white;
  border: 1px solid #a2a9b1;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-height: 240px;
  overflow-y: auto;
}
.c2c-desc-suggestions li {
  padding: 7px 10px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.c2c-desc-suggestions li:hover,
.c2c-desc-suggestion--active { background: #eaf3ff; }
.c2c-desc-suggestion-native { flex: 1; min-width: 0; }
.c2c-desc-suggestion--present { color: #72777d; }
.c2c-desc-suggestion-added { font-size: 11px; color: #72777d; }
.c2c-desc-nomatch {
  display: block; margin-top: 4px; color: #54595d; font-size: 12px;
  /* A pasted 250-character word has no break opportunity, so without
     this it pushes the card wider than the page and the whole form
     gets a horizontal scrollbar. */
  overflow-wrap: anywhere;
  min-width: 0;
}

@media (max-width: 540px) {
  .c2c-desc-add-row { flex-direction: column; align-items: stretch; }
}
</style>
